import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { useAppStateProvider } from "../../../contextProviders/AppStateProvider";
import createTranslator from "../../../util/translationHelper";
import messages from "../translations/messages";
import useAccountingFeatureFlag from "../useAccountingFeatureFlag";
import useAccountingGuard from "../useAccountingGuard";
import { SUBSCRIPTION_STATUS } from "../constants";
import getToken from "../../../util/tokenGenerator";
import { handleTokenRequest } from "../../../util/helper";
import helper from "./pricingHelper";
import {
  getChangePlanData,
  setChangePlanData,
  clearChangePlanData,
  isChangePlanStored,
} from "../qbo_AccountingHelper";

function usePricing(props) {
  const { lang, history } = props;
  const BLOCK = useRef("qbo-pricing");
  const { appState, updateAppStateLocal } = useAppStateProvider();
  const isChangePlanMode = appState?.changePlanMode === true;

  useAccountingFeatureFlag({ lang, history });
  const { isReady } = useAccountingGuard({ lang, history, skip: isChangePlanMode });
  const { instance, inProgress, accounts } = useMsal();
  const getTokenHandler = () => getToken(instance, inProgress, accounts);
  const tokenhandler = async () => await handleTokenRequest(getTokenHandler);

  const subscription = appState?.qboSubscription;
  const currentFrequency = subscription?.billing_frequency;
  const nextBillingDate = subscription?.next_billing_date;

  const translate = useMemo(() => {
    return createTranslator(messages);
  }, [createTranslator, messages]);

  const storedData = getChangePlanData();
  const isInChangePlanFlow = isChangePlanMode || isChangePlanStored();

  // Store current plan details on first entry so they survive reloads
  useEffect(() => {
    if (isInChangePlanFlow && currentFrequency && !storedData?.currentFrequency) {
      setChangePlanData({
        currentFrequency,
        nextBillingDate: nextBillingDate || null,
      });
    }
  }, [isInChangePlanFlow, currentFrequency, nextBillingDate]);

  const resolvedCurrentFrequency = currentFrequency || storedData?.currentFrequency;
  const resolvedNextBillingDate = nextBillingDate || storedData?.nextBillingDate;
  const [selectedPlan, setSelectedPlan] = useState(
    isInChangePlanFlow
      ? resolvedCurrentFrequency || null
      : null
  );
  const [trialEligible, setTrialEligible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [error, setError] = useState(null);

  const hasChanged = isChangePlanMode && selectedPlan !== resolvedCurrentFrequency;
  // OMS flow: subscription already created via OMS, skip Pricing Page entirely
  useEffect(() => {
    if (!isReady) return;
    if (subscription?.status === SUBSCRIPTION_STATUS.PENDING) {
      history.replace(`/${lang}/qbo-accounting/terms`);
    }
  }, [isReady, subscription, lang, history]);

  useEffect(() => {
    if (!isReady || isChangePlanMode) return;

    const fetchEligibility = async () => {
      try {
        const eligible = await helper.fetchTrialEligibility(tokenhandler);
        setTrialEligible(eligible);
      } catch {
        setTrialEligible(false);
      }
    };

    fetchEligibility();
  }, [isReady, isChangePlanMode]);

  const changePlanBannerText = useMemo(() => {
    if (!isChangePlanMode || !hasChanged || !resolvedNextBillingDate) return null;

    const formattedDate = new Date(resolvedNextBillingDate).toLocaleDateString(
      lang === "fr" ? "fr-CA" : "en-CA",
      { year: "numeric", month: "long", day: "numeric" }
    );

    if (resolvedCurrentFrequency === helper.BILLING_FREQUENCY.YEARLY) {
      return translate("change-plan-banner-yearly-to-monthly").replace("{date}", formattedDate);
    }
    return translate("change-plan-banner-monthly-to-yearly").replace("{date}", formattedDate);
  }, [isChangePlanMode, hasChanged, resolvedCurrentFrequency, resolvedNextBillingDate, lang, translate]);

  const handleSelectPlan = useCallback((plan) => {
    setSelectedPlan(plan);
    setError(null);
  }, []);

  const handleBack = useCallback(() => {
    setIsNavigating(true);
    clearChangePlanData();
    updateAppStateLocal({ changePlanMode: false, changePlanNewFrequency: null });
    history.push(`/${lang}/qbo-accounting`);
  }, [lang, history, updateAppStateLocal]);

  const handleSubmit = useCallback(async () => {
    if (!selectedPlan || isSubmitting) return;

    if (isChangePlanMode) {
      if (!hasChanged) return;
      updateAppStateLocal({ changePlanNewFrequency: selectedPlan });
      setChangePlanData({ active: true, newFrequency: selectedPlan });
      history.push(`/${lang}/qbo-accounting/terms`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await helper.submitPlanSelect(selectedPlan, trialEligible, tokenhandler);

      if (response?.status === 200 || response?.status === 201) {
        history.push(`/${lang}/qbo-accounting/terms`);
      } else {
        setError(helper.getErrorMessage(response?.status, translate));
      }
    } catch (err) {
      const status = err?.response?.status;
      setError(helper.getErrorMessage(status, translate));
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedPlan, isSubmitting, isChangePlanMode, hasChanged, trialEligible, lang, history, translate, tokenhandler, updateAppStateLocal]);

  // Clean up changePlanData on unmount if navigating away from QBO
  useEffect(() => {
    return () => {
      if (!window.location.pathname.includes("/qbo-accounting/")) {
        clearChangePlanData();
        updateAppStateLocal({ changePlanMode: false, changePlanNewFrequency: null });
      }
    };
  }, []);

  return {
    BLOCK,
    translate,
    isReady,
    isNavigating,
    selectedPlan,
    trialEligible,
    isSubmitting,
    error,
    isChangePlanMode,
    hasChanged,
    changePlanBannerText,
    handleSelectPlan,
    handleBack,
    handleSubmit,
  };
}

const Hook = {
  usePricing,
};

export default Hook;
